import { useState, type CSSProperties } from 'react';
import type { Concept } from '../help/types.js';

interface Props {
  concept: Concept;
  /** slug → display name lookup so related-link text reads as the concept's name. */
  nameBySlug: Map<string, string>;
}

const cardStyle: CSSProperties = {
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  background: 'var(--loop-neutral-0)',
  padding: 'var(--loop-space-200)',
  marginBottom: 'var(--loop-space-150)',
};

const headerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 'var(--loop-space-100)',
  marginBottom: 'var(--loop-space-100)',
};

const nameStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 664,
  color: 'var(--loop-neutral-1100)',
  margin: 0,
};

const metaStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--loop-neutral-700)',
  marginTop: 'var(--loop-space-100)',
};

const metaLabelStyle: CSSProperties = {
  fontWeight: 528,
  marginRight: 'var(--loop-space-050)',
};

const toggleStyle: CSSProperties = {
  marginTop: 'var(--loop-space-100)',
  background: 'none',
  border: 'none',
  color: 'var(--loop-brand-600)',
  cursor: 'pointer',
  padding: 0,
  fontSize: '0.8125rem',
};

const expandedStyle: CSSProperties = {
  marginTop: 'var(--loop-space-150)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-900)',
  lineHeight: 1.5,
};

export function ConceptCard({ concept, nameBySlug }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <article id={concept.slug} style={cardStyle}>
      <div style={headerRowStyle}>
        <h4 style={nameStyle}>{concept.name}</h4>
      </div>
      <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--loop-neutral-900)', lineHeight: 1.5 }}>
        {concept.oneLiner}
      </p>
      {concept.seenOn.length > 0 && (
        <div style={metaStyle}>
          <span style={metaLabelStyle}>Where you&apos;ll see it:</span>
          {concept.seenOn.join(', ')}
        </div>
      )}
      {concept.related.length > 0 && (
        <div style={metaStyle}>
          <span style={metaLabelStyle}>Related:</span>
          {concept.related.map((slug, i) => (
            <span key={slug}>
              {i > 0 && ', '}
              <a href={`#${slug}`} style={{ color: 'var(--loop-brand-600)' }}>{nameBySlug.get(slug) ?? slug}</a>
            </span>
          ))}
        </div>
      )}
      {concept.expanded && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            style={toggleStyle}
            aria-expanded={open}
            aria-controls={`${concept.slug}-expanded`}
          >
            {open ? 'Hide details ▴' : 'Show more ▾'}
          </button>
          {open && (
            <div id={`${concept.slug}-expanded`} style={expandedStyle}>
              {concept.expanded}
            </div>
          )}
        </>
      )}
      {concept.externalRefs && concept.externalRefs.length > 0 && (
        <div style={metaStyle}>
          <span style={metaLabelStyle}>Source of truth:</span>
          {concept.externalRefs.map((ref, i) => (
            <span key={ref.href}>
              {i > 0 && ', '}
              <a href={ref.href} style={{ color: 'var(--loop-brand-600)' }} target="_blank" rel="noopener noreferrer">
                {ref.label}
              </a>
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
